import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatApiError, API_BASE } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { ArrowLeft, FileDown, Send, Ban, Plus, Save, Trash2, CreditCard, Calendar as CalendarIcon, ExternalLink, Mail, Landmark, Copy, CheckCircle2, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { INVOICE_STATUS, INVOICE_TYPE, GBP } from './constants';
import SendEmailDialog from '../../components/SendEmailDialog';


function fmtDate(iso) {
    if (!iso) return null;
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function DatePickerField({ label, value, onChange, disabled, testId }) {
    const [open, setOpen] = useState(false);
    const dateVal = value ? new Date(value + 'T12:00:00') : undefined;
    const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    return (
        <div>
            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
            <Popover open={open && !disabled} onOpenChange={v => !disabled && setOpen(v)}>
                <PopoverTrigger asChild>
                    <Button variant="outline" disabled={disabled}
                            className="mt-1.5 h-9 w-full justify-start text-left font-normal text-[13px]"
                            data-testid={testId}>
                        <CalendarIcon size={14} className="mr-2 text-[color:var(--bm-text-subtle)]" />
                        {value ? fmtDate(value) : <span className="text-[color:var(--bm-text-subtle)]">Pick a date</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateVal} initialFocus
                              onSelect={d => { if (d) { onChange(toIso(d)); setOpen(false); } }} />
                </PopoverContent>
            </Popover>
        </div>
    );
}

export default function InvoiceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [inv, setInv] = useState(null);
    const [saving, setSaving] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const [emailDefaults, setEmailDefaults] = useState({ to: '', subject: '' });
    const [emailSending, setEmailSending] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/invoices/${id}`);
            const data = r.data;
            // Migrate legacy single payment link into the two-option model for display
            if (!data.bank_transfer_url && !data.card_payment_url && data.payment_link_url) {
                if (data.payment_link_provider === 'stripe' || data.payment_link_provider === 'gocardless') {
                    data.card_payment_url = data.payment_link_url;
                    data.card_payment_provider = data.payment_link_provider;
                } else {
                    data.bank_transfer_url = data.payment_link_url;
                }
            }
            setInv(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); if (e?.response?.status === 404) navigate('/invoices'); }
    }, [id, navigate]);
    useEffect(() => { load(); }, [load]);

    const update = (patch) => setInv(v => ({ ...v, ...patch }));

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                line_items: (inv.line_items || []).map(li => ({
                    description: li.description,
                    qty: Number(li.qty) || 0,
                    unit_price: Number(li.unit_price) || 0,
                    vat_treatment: li.vat_treatment || (li.vat_applies !== false ? 'standard' : 'zero'),
                })),
                notes: inv.notes,
                bank_transfer_url: inv.bank_transfer_url || null,
                card_payment_url: inv.card_payment_url || null,
                card_payment_provider: inv.card_payment_provider || null,
                issue_date: inv.issue_date,
                due_date: inv.due_date,
                vat_rate: inv.vat_rate === '' || inv.vat_rate == null ? 20 : Number(inv.vat_rate),
            };
            const r = await api.patch(`/invoices/${id}`, body);
            setInv({ ...r.data, payments: inv.payments });
            toast.success('Invoice saved');
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const markSent = async () => {
        try { const r = await api.patch(`/invoices/${id}`, { status: 'sent' }); setInv({ ...r.data, payments: inv.payments }); toast.success('Marked as sent'); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const cancelInv = async () => {
        if (!confirm('Cancel this invoice?')) return;
        try { const r = await api.patch(`/invoices/${id}`, { status: 'cancelled' }); setInv({ ...r.data, payments: inv.payments }); toast.success('Cancelled'); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const openEmailDialog = async () => {
        let to = '';
        // 1) Prefer a primary client contact with an email
        try {
            const r = await api.get(`/clients/${inv.client_id}/contacts`);
            const contacts = r.data.items || [];
            const primary = contacts.find(c => c.is_primary && c.email) || contacts.find(c => c.email);
            to = primary?.email || '';
        } catch { /* non-fatal */ }
        // 2) Fall back to the email stored on the client record itself
        if (!to) {
            try {
                const cr = await api.get(`/clients/${inv.client_id}`);
                to = cr.data?.email || '';
            } catch { /* non-fatal */ }
        }
        setEmailDefaults({ to, subject: `Invoice ${inv.invoice_number} — Beyond Measure` });
        setEmailOpen(true);
    };

    const sendEmail = async (to, subject, message, attachPdf) => {
        setEmailSending(true);
        try {
            await api.post(`/invoices/${id}/send-email`, { to, subject, message: message || null, attach_pdf: attachPdf });
            toast.success(`Email sent to ${to}`);
            setEmailOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || 'Failed to send'); }
        finally { setEmailSending(false); }
    };

    const addLine = () => update({ line_items: [...(inv.line_items || []), { description: '', qty: 1, unit_price: 0, vat_applies: true, vat_treatment: 'standard' }] });
    const updateLine = (idx, patch) => {
        const li = [...inv.line_items]; li[idx] = { ...li[idx], ...patch }; update({ line_items: li });
    };
    const removeLine = (idx) => update({ line_items: inv.line_items.filter((_, i) => i !== idx) });

    // Live totals — always computed, handles inv=null gracefully (loading state)
    const liveVatRate = inv?.vat_rate === '' || inv?.vat_rate == null ? 20 : Number(inv?.vat_rate);
    const live = useMemo(() => {
        if (!inv) return { subtotal: 0, vat_amount: 0, total: 0, balance_due: 0 };
        let subtotal = 0, vatAmount = 0;
        for (const li of (inv.line_items || [])) {
            const line = Math.round((Number(li.qty) || 0) * (Number(li.unit_price) || 0) * 100) / 100;
            subtotal = Math.round((subtotal + line) * 100) / 100;
            const charges = li.vat_treatment ? li.vat_treatment === 'standard' : li.vat_applies !== false;
            if (charges) vatAmount = Math.round((vatAmount + line * liveVatRate / 100) * 100) / 100;
        }
        const total = Math.round((subtotal + vatAmount) * 100) / 100;
        const balanceDue = Math.max(0, Math.round((total - (Number(inv.amount_paid) || 0)) * 100) / 100);
        return { subtotal, vat_amount: vatAmount, total, balance_due: balanceDue };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inv?.line_items, liveVatRate, inv?.amount_paid]);

    if (!inv) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading…</div>;
    const isDraft = inv.status === 'draft';
    // Financially-locked statuses — line items, dates and VAT can't be edited
    // here once the invoice is closed. (`paid` is editable elsewhere via a
    // credit note flow, not by directly mutating the line items.)
    const LOCKED = ['paid', 'void', 'cancelled'];
    const isEditable = !LOCKED.includes(inv.status);
    const showSentWarn = isEditable && !isDraft;   // sent / part_paid / overdue
    const st = INVOICE_STATUS[inv.status] || { label: inv.status, tone: '' };

    return (
        <div data-testid="invoice-detail-page" className="bm-fade-in">
            <PageHeader
                title={inv.invoice_number}
                description={`${inv.client_name} · ${INVOICE_TYPE[inv.type]?.label || inv.type}`}
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" onClick={() => navigate('/invoices')} className="h-9" data-testid="invoice-back-btn">
                            <ArrowLeft size={15} className="mr-1" /> Back
                        </Button>
                        <Button variant="outline" onClick={() => window.open(`${API_BASE}/invoices/${id}/pdf`, '_blank')} className="h-9" data-testid="invoice-pdf-btn">
                            <FileDown size={15} className="mr-1" /> PDF
                        </Button>
                        {isEditable && <Button onClick={save} disabled={saving} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="invoice-save-btn"><Save size={15} className="mr-1" /> Save</Button>}
                        {isDraft && <Button onClick={markSent} className="h-9" style={{ background: 'var(--bm-accent-600)' }} data-testid="invoice-send-btn"><Send size={15} className="mr-1" /> Mark sent</Button>}
                        {inv.status !== 'cancelled' && (
                            <Button onClick={openEmailDialog} variant="outline" className="h-9" data-testid="invoice-email-btn">
                                <Mail size={15} className="mr-1" /> Send by email
                            </Button>
                        )}
                        {!['paid', 'cancelled'].includes(inv.status) && (
                            <Button onClick={() => setPayOpen(true)} className="h-9" style={{ background: 'var(--bm-accent-600)' }} data-testid="invoice-pay-btn">
                                <CreditCard size={15} className="mr-1" /> Record payment
                            </Button>
                        )}
                        {inv.status !== 'cancelled' && inv.status !== 'paid' && (
                            <Button variant="outline" onClick={cancelInv} className="h-9" data-testid="invoice-cancel-btn"><Ban size={15} className="mr-1" /> Cancel</Button>
                        )}
                    </div>
                }
            >
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <span className={`bm-pill ${st.tone} border`}>{st.label}</span>
                    {inv.contract_id && (
                        <button onClick={() => navigate(`/contracts/${inv.contract_id}`)} className="text-[12px] text-[color:var(--bm-primary)] hover:underline">from contract</button>
                    )}
                    {inv.client_vat_number && (
                        <span className="text-[12px] text-[color:var(--bm-text-muted)]" data-testid="invoice-client-vat-number">Client VAT: {inv.client_vat_number}</span>
                    )}
                </div>
            </PageHeader>

            <div className="px-4 sm:px-8 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    {/* Line items */}
                    <Card title="Line items" actions={isEditable && <Button size="sm" variant="outline" onClick={addLine} data-testid="invoice-add-line-btn"><Plus size={13} className="mr-1" /> Add line</Button>}>
                        {showSentWarn && (
                            <div className="mb-3 text-[12px] px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800">
                                ⚠️ This invoice has been issued. Editing line items, dates or VAT will
                                <strong> reverse and re-post the General Ledger entry</strong> automatically.
                                If a customer has the original PDF, send them a fresh copy.
                            </div>
                        )}
                        <div className="space-y-2">
                            {(inv.line_items || []).map((li, idx) => {
                                const treatment = li.vat_treatment || (li.vat_applies !== false ? 'standard' : 'zero');
                                const lineTotal = (Number(li.qty) || 0) * (Number(li.unit_price) || 0);
                                return (
                                    <div key={idx} data-testid={`invoice-line-${idx}`}>
                                        {/* Desktop / tablet row */}
                                        <div className="hidden sm:grid grid-cols-12 gap-2 items-center">
                                            <div className="col-span-4">
                                                <Input value={li.description || ''} onChange={e => updateLine(idx, { description: e.target.value })} disabled={!isEditable} className="h-9" />
                                            </div>
                                            <div className="col-span-1"><Input type="number" step="0.1" value={li.qty ?? 1} onChange={e => updateLine(idx, { qty: e.target.value })} disabled={!isEditable} className="h-9" /></div>
                                            <div className="col-span-2"><Input type="number" step="0.01" value={li.unit_price ?? 0} onChange={e => updateLine(idx, { unit_price: e.target.value })} disabled={!isEditable} className="h-9" /></div>
                                            <div className="col-span-2">
                                                <Select value={treatment} onValueChange={v => updateLine(idx, { vat_treatment: v, vat_applies: v === 'standard' })} disabled={!isEditable}>
                                                    <SelectTrigger className="h-9" data-testid={`invoice-line-vat-${idx}`}><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="standard">Standard VAT</SelectItem>
                                                        <SelectItem value="zero">Zero-rated</SelectItem>
                                                        <SelectItem value="exempt">Exempt</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="col-span-2 text-right text-[13px] font-medium">{GBP(lineTotal)}</div>
                                            <div className="col-span-1 text-right">{isEditable && <button onClick={() => removeLine(idx)} className="text-[color:var(--bm-text-subtle)] hover:text-red-600 p-1"><Trash2 size={14} /></button>}</div>
                                        </div>
                                        {/* Mobile stacked card */}
                                        <div className="sm:hidden rounded-lg border border-[color:var(--bm-border)] p-3 space-y-2">
                                            <div className="flex items-start gap-2">
                                                <Input value={li.description || ''} onChange={e => updateLine(idx, { description: e.target.value })} disabled={!isEditable} className="h-9 flex-1" placeholder="Description" />
                                                {isEditable && <button onClick={() => removeLine(idx)} className="text-[color:var(--bm-text-subtle)] hover:text-red-600 p-2 shrink-0"><Trash2 size={15} /></button>}
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <Label className="text-[10.5px] text-[color:var(--bm-text-subtle)]">Qty</Label>
                                                    <Input type="number" step="0.1" value={li.qty ?? 1} onChange={e => updateLine(idx, { qty: e.target.value })} disabled={!isEditable} className="h-9 mt-1" />
                                                </div>
                                                <div>
                                                    <Label className="text-[10.5px] text-[color:var(--bm-text-subtle)]">Unit price</Label>
                                                    <Input type="number" step="0.01" value={li.unit_price ?? 0} onChange={e => updateLine(idx, { unit_price: e.target.value })} disabled={!isEditable} className="h-9 mt-1" />
                                                </div>
                                            </div>
                                            <Select value={treatment} onValueChange={v => updateLine(idx, { vat_treatment: v, vat_applies: v === 'standard' })} disabled={!isEditable}>
                                                <SelectTrigger className="h-9" data-testid={`invoice-line-vat-mobile-${idx}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="standard">Standard VAT</SelectItem>
                                                    <SelectItem value="zero">Zero-rated</SelectItem>
                                                    <SelectItem value="exempt">Exempt</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <div className="text-right text-[13px] font-semibold text-[color:var(--bm-primary)]">{GBP(lineTotal)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                            {(inv.line_items || []).length === 0 && <div className="text-[12.5px] text-[color:var(--bm-text-muted)] py-3 text-center">No line items</div>}
                        </div>
                    </Card>

                    <Card title="Totals">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px] max-w-sm ml-auto">
                            <div className="text-[color:var(--bm-text-muted)]">Subtotal</div><div className="text-right">{GBP(live.subtotal)}</div>
                            <div className="text-[color:var(--bm-text-muted)]">VAT ({liveVatRate}%)</div><div className="text-right">{GBP(live.vat_amount)}</div>
                            <div className="font-semibold text-[color:var(--bm-primary)]">Total</div><div className="text-right font-semibold text-[color:var(--bm-primary)]">{GBP(live.total)}</div>
                            <div className="text-[color:var(--bm-text-muted)]">Amount paid</div><div className="text-right text-[color:var(--bm-accent-600)]">{GBP(inv.amount_paid)}</div>
                            <div className="font-display font-semibold text-[15px]">Balance due</div>
                            <div className={`text-right font-display font-semibold text-[15px] ${live.balance_due > 0 ? 'text-[color:var(--bm-text)]' : 'text-[color:var(--bm-accent-600)]'}`}>{GBP(live.balance_due)}</div>
                        </div>
                    </Card>

                    <Card title="Payments received">
                        {(inv.payments || []).length === 0 ? (
                            <div className="text-[12.5px] text-[color:var(--bm-text-muted)] py-4 text-center">No payments recorded yet.</div>
                        ) : (
                            <div className="overflow-x-auto">
                            <table className="w-full text-[13px]">
                                <thead className="text-[color:var(--bm-text-muted)] border-b border-[color:var(--bm-border)]">
                                    <tr><th className="text-left py-2">Date</th><th className="text-left">Method</th><th className="text-left">Reference</th><th className="text-right">Amount</th></tr>
                                </thead>
                                <tbody>
                                    {inv.payments.map(p => (
                                        <tr key={p.id} className="border-b border-[color:var(--bm-border)] last:border-0">
                                            <td className="py-2 whitespace-nowrap">{p.payment_date}</td>
                                            <td className="text-[color:var(--bm-text-muted)] whitespace-nowrap">{p.method || '—'}</td>
                                            <td className="text-[color:var(--bm-text-muted)] whitespace-nowrap">{p.reference || '—'}</td>
                                            <td className="text-right font-medium text-[color:var(--bm-accent-600)] whitespace-nowrap">{GBP(p.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </Card>
                </div>

                <div className="space-y-5">
                    {/* GoCardless Direct Debit */}
                    <GoCardlessPanel inv={inv} clientId={inv.client_id} onRefresh={load} />

                    <Card title="Invoice details">
                        <div className="space-y-3 text-[13px]">
                            <DatePickerField label="Issue date" value={inv.issue_date} disabled={!isDraft} onChange={v => update({ issue_date: v })} testId="invoice-issue-date-picker" />
                            <DatePickerField label="Due date"   value={inv.due_date}   disabled={!isDraft} onChange={v => update({ due_date: v })} testId="invoice-due-date-picker" />
                            <F label="VAT rate (%)" value={inv.vat_rate} disabled={!isDraft} onChange={v => update({ vat_rate: v })} type="number" />
                            <div className="pt-1 border-t border-[color:var(--bm-border)]">
                                <Label className="text-[12px] font-semibold text-[color:var(--bm-text)]">Payment options</Label>
                                <p className="text-[11px] text-[color:var(--bm-text-subtle)] mt-0.5">Shown on the invoice PDF & email. Add either or both.</p>
                                {inv.recurring_schedule_id && (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/recurring/${inv.recurring_schedule_id}`)}
                                        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--bm-primary)]/10 px-2.5 py-1 text-[11px] font-medium text-[color:var(--bm-primary)] hover:bg-[color:var(--bm-primary)]/15 transition-colors"
                                        title="These payment links were set on the recurring schedule and apply to every invoice it generates."
                                        data-testid="invoice-recurring-payment-badge">
                                        <RefreshCw size={11} /> Payment options inherited from schedule
                                    </button>
                                )}
                            </div>
                            {/* Option 1 — Bank transfer (Monzo) */}
                            <div className="rounded-lg border border-[color:var(--bm-border)] p-3">
                                <div className="flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-text)] mb-1.5">
                                    <Landmark size={13} className="text-[color:var(--bm-primary)]" /> Bank transfer (Monzo)
                                </div>
                                <Input value={inv.bank_transfer_url || ''} onChange={e => update({ bank_transfer_url: e.target.value })}
                                       disabled={!isEditable} className="h-9" placeholder="https://monzo.me/…"
                                       data-testid="invoice-bank-transfer-input" />
                                {inv.bank_transfer_url && (
                                    <a href={inv.bank_transfer_url} target="_blank" rel="noopener noreferrer"
                                       className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-primary)] hover:underline"
                                       data-testid="invoice-bank-transfer-open">
                                        <ExternalLink size={12} /> Open bank transfer link
                                    </a>
                                )}
                            </div>
                            {/* Option 2 — Card / Direct Debit */}
                            <div className="rounded-lg border border-[color:var(--bm-border)] p-3">
                                <div className="flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-text)] mb-1.5">
                                    <CreditCard size={13} className="text-[color:var(--bm-accent-600)]" /> Card / Direct Debit
                                </div>
                                <Select value={inv.card_payment_provider || '_none_'} onValueChange={v => update({ card_payment_provider: v === '_none_' ? null : v })} disabled={!isEditable}>
                                    <SelectTrigger className="h-9" data-testid="invoice-card-provider-select">
                                        <SelectValue placeholder="Choose provider…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none_">None</SelectItem>
                                        <SelectItem value="stripe">Stripe (card)</SelectItem>
                                        <SelectItem value="gocardless">GoCardless (Direct Debit)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input value={inv.card_payment_url || ''} onChange={e => update({ card_payment_url: e.target.value })}
                                       disabled={!isEditable} className="h-9 mt-2" placeholder="https://buy.stripe.com/… or pay.gocardless.com/…"
                                       data-testid="invoice-card-payment-input" />
                                {inv.card_payment_url && (
                                    <a href={inv.card_payment_url} target="_blank" rel="noopener noreferrer"
                                       className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-accent-600)] hover:underline"
                                       data-testid="invoice-card-payment-open">
                                        <ExternalLink size={12} /> Open payment link
                                    </a>
                                )}
                            </div>
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Notes</Label>
                                <Textarea value={inv.notes || ''} onChange={e => update({ notes: e.target.value })} disabled={!isEditable} className="mt-1.5" rows={3} />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            <PaymentDialog open={payOpen} onClose={() => setPayOpen(false)} invoice={inv} onRecorded={load} />

            <SendEmailDialog
                open={emailOpen}
                onClose={() => setEmailOpen(false)}
                defaultTo={emailDefaults.to}
                defaultSubject={emailDefaults.subject}
                showAttachPdf
                defaultAttachPdf
                sending={emailSending}
                onSend={sendEmail}
                bodyPreview={
                    <>
                        <div className="flex justify-between"><span>{inv.invoice_number}</span><span className="font-semibold">{inv.client_name}</span></div>
                        <div className="flex justify-between"><span>Amount due</span><span className="font-semibold text-[color:var(--bm-accent-600)]">{GBP(inv.balance_due)}</span></div>
                        <div className="flex justify-between"><span>Due</span><span>{inv.due_date}</span></div>
                        {(inv.bank_transfer_url || inv.card_payment_url || inv.payment_link_url) && <div className="text-[color:var(--bm-primary)]">+ Payment options included</div>}
                    </>
                }
            />
        </div>
    );
}

// ── GoCardless Direct Debit panel ─────────────────────────────────────────────
function GoCardlessPanel({ inv, clientId, onRefresh }) {
    const [mandate, setMandate]   = useState(null);
    const [loading, setLoading]   = useState(false);
    const [setupUrl, setSetupUrl] = useState(null);
    const [copied, setCopied]     = useState(false);
    const [charging, setCharging] = useState(false);

    const isPaid     = inv.status === 'paid' || inv.status === 'cancelled';
    const gcStatus   = inv.gocardless_status;
    const balanceDue = parseFloat(inv.balance_due || 0);

    const fetchMandate = useCallback(async () => {
        if (!clientId) return;
        try {
            const r = await api.get(`/gocardless/status/${clientId}`);
            setMandate(r.data);
        } catch { /* silent */ }
    }, [clientId]);

    useEffect(() => { fetchMandate(); }, [fetchMandate]);

    const handleSetup = async () => {
        setLoading(true);
        try {
            const r = await api.post(`/gocardless/setup/${inv.id}`);
            setSetupUrl(r.data.authorisation_url);
            onRefresh();  // refresh invoice status — but setupUrl stays in local state
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'GoCardless setup failed');
        } finally { setLoading(false); }
    };

    const handleCharge = async () => {
        if (!confirm(`Collect ${GBP(balanceDue)} via Direct Debit now?`)) return;
        setCharging(true);
        try {
            const r = await api.post(`/gocardless/charge/${inv.id}`);
            toast.success(`Direct Debit initiated — collecting ${GBP(r.data.amount)} on ${r.data.charge_date || 'next working day'}`);
            onRefresh();
            fetchMandate();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Charge failed');
        } finally { setCharging(false); }
    };

    const copyUrl = (url) => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Link copied — send this to your client');
        setTimeout(() => setCopied(false), 3000);
    };

    const hasActiveMandateForClient = mandate?.has_mandate && mandate?.mandate_status === 'active';
    const isPending      = gcStatus === 'mandate_pending';
    const isPayPending   = gcStatus === 'payment_pending';
    const isConfirmed    = gcStatus === 'payment_confirmed';
    const isFailed       = gcStatus === 'payment_failed';
    const hasMandateReady = gcStatus === 'mandate_active' || hasActiveMandateForClient;

    return (
        <div data-testid="gocardless-panel" className="rounded-xl border p-4 space-y-3"
            style={{ background: 'linear-gradient(135deg,#f0f9ff 0%,#e8f4fd 100%)', borderColor: '#bfdbfe' }}>

            <div className="flex items-center gap-2">
                <Landmark size={16} className="text-blue-600 shrink-0" />
                <span className="text-[13px] font-semibold text-blue-900">Direct Debit via GoCardless</span>
            </div>

            {/* Payment confirmed */}
            {isConfirmed && (
                <div className="flex items-center gap-2 text-[12px] font-medium px-3 py-2 rounded-lg border text-emerald-600 bg-emerald-50 border-emerald-200">
                    <CheckCircle2 size={13} /> Payment collected via Direct Debit
                </div>
            )}

            {/* Payment processing */}
            {isPayPending && (
                <div className="flex items-center gap-2 text-[12px] font-medium px-3 py-2 rounded-lg border text-blue-600 bg-blue-50 border-blue-200">
                    <Loader2 size={13} className="animate-spin" /> Payment submitted — clears in 2–3 working days
                </div>
            )}

            {/* Mandate pending: show the link to send */}
            {!isPaid && isPending && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-[12px] font-medium px-3 py-2 rounded-lg border text-amber-700 bg-amber-50 border-amber-200">
                        <Loader2 size={13} className="animate-spin" /> Waiting for your client to authorise
                    </div>

                    {/* URL visible right after generation */}
                    {setupUrl && (
                        <div className="space-y-1.5">
                            <p className="text-[11.5px] font-semibold text-blue-800">Copy this link and send it to your client:</p>
                            <div className="flex gap-2">
                                <input readOnly value={setupUrl} onClick={e => e.target.select()}
                                    className="flex-1 text-[11px] rounded-lg border border-blue-200 bg-white px-2 py-1.5 text-blue-900 truncate" />
                                <Button size="sm" variant="outline" onClick={() => copyUrl(setupUrl)}
                                    className="shrink-0 h-8 border-blue-300" data-testid="gc-copy-btn">
                                    {copied ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Copy size={13} />}
                                </Button>
                            </div>
                            <a href={setupUrl} target="_blank" rel="noopener noreferrer"
                               className="text-[11px] text-blue-500 hover:underline flex items-center gap-1">
                                <ExternalLink size={10} /> Preview the GoCardless page
                            </a>
                        </div>
                    )}

                    {/* Returning to page — offer to regenerate */}
                    {!setupUrl && (
                        <div className="space-y-1.5">
                            <p className="text-[11.5px] text-blue-700">A mandate link was already sent. Regenerate if your client needs a new one.</p>
                            <Button onClick={handleSetup} disabled={loading} size="sm" variant="outline"
                                className="w-full h-8 text-[12px] border-blue-300 text-blue-700" data-testid="gc-regenerate-btn">
                                {loading ? <><Loader2 size={12} className="mr-1.5 animate-spin" />Generating…</> : <><RefreshCw size={12} className="mr-1.5" />Regenerate link</>}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* No mandate, nothing pending → offer initial setup */}
            {!isPaid && !isPending && !isPayPending && !isConfirmed && !hasMandateReady && !isFailed && (
                <div className="space-y-3">
                    <p className="text-[12px] text-blue-800 leading-relaxed">
                        Generate a link to send to <strong>{inv.client_name}</strong>. They authorise their bank once — you then collect this and all future invoices with one click.
                    </p>
                    <Button onClick={handleSetup} disabled={loading || balanceDue <= 0} size="sm"
                        className="w-full h-9 text-[13px]" style={{ background: '#1a56db' }} data-testid="gc-setup-btn">
                        {loading ? <><Loader2 size={13} className="mr-1.5 animate-spin" />Generating…</> : <><Landmark size={13} className="mr-1.5" />Generate mandate link</>}
                    </Button>
                </div>
            )}

            {/* Active mandate → collect */}
            {!isPaid && !isPending && !isPayPending && !isConfirmed && (hasMandateReady || isFailed) && (
                <div className="space-y-2">
                    {isFailed ? (
                        <div className="flex items-center gap-2 text-[12px] font-medium px-3 py-2 rounded-lg border text-red-600 bg-red-50 border-red-200">
                            <AlertCircle size={13} /> {`Payment failed: ${inv.gocardless_failure_reason || 'see GoCardless dashboard'}`}
                        </div>
                    ) : (
                        <div className="flex items-center gap-1.5 text-[12px] text-emerald-700 font-medium">
                            <CheckCircle2 size={12} /> Active mandate on file
                        </div>
                    )}
                    <Button onClick={handleCharge} disabled={charging || balanceDue <= 0} size="sm"
                        className="w-full h-9 text-[13px]" style={{ background: '#1a56db' }} data-testid="gc-charge-btn">
                        {charging ? <><Loader2 size={13} className="mr-1.5 animate-spin" />Submitting…</> : <><Landmark size={13} className="mr-1.5" />Collect {GBP(balanceDue)} via Direct Debit</>}
                    </Button>
                </div>
            )}

            <p className="text-[10.5px] text-blue-500">Powered by GoCardless · UK Bacs Direct Debit · FCA regulated</p>
        </div>
    );
}

function Card({ title, children, actions }) {
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="font-display text-[15px] font-semibold">{title}</div>
                {actions}
            </div>
            {children}
        </div>
    );
}
function F({ label, value, onChange, type = 'text', disabled }) {
    return (
        <div>
            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
            <Input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} disabled={disabled} className="mt-1.5 h-9" />
        </div>
    );
}

function PaymentDialog({ open, onClose, invoice, onRecorded }) {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('');
    const [ref, setRef] = useState('');
    const [payDate, setPayDate] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (open) {
            setAmount(invoice?.balance_due ? String(invoice.balance_due) : '');
            setMethod(''); setRef(''); setPayDate(new Date().toISOString().slice(0, 10)); setNotes('');
        }
    }, [open, invoice]);

    const submit = async (e) => {
        e.preventDefault();
        const a = Number(amount);
        if (!a || a <= 0) return toast.error('Enter an amount > 0');
        try {
            await api.post('/payments', { invoice_id: invoice.id, amount: a, method: method || null, reference: ref || null, notes: notes || null, payment_date: payDate });
            toast.success(`Recorded ${a.toFixed(2)}`);
            onRecorded();
            onClose();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-[460px]" data-testid="payment-dialog">
                <DialogHeader>
                    <DialogTitle>Record payment</DialogTitle>
                    <DialogDescription className="text-[12.5px] text-[color:var(--bm-text-muted)]">Balance due: {GBP(invoice?.balance_due)}.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3 mt-2">
                    <F label="Amount *" type="number" value={amount} onChange={setAmount} />
                    <DatePickerField label="Payment date" value={payDate} onChange={setPayDate} testId="payment-date-picker" />
                    <div>
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Method</Label>
                        <Select value={method || ''} onValueChange={setMethod}>
                            <SelectTrigger className="mt-1.5 h-10" data-testid="payment-method-select"><SelectValue placeholder="Select method" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Monzo">Monzo</SelectItem>
                                <SelectItem value="Bank transfer">Bank transfer</SelectItem>
                                <SelectItem value="Card">Card</SelectItem>
                                <SelectItem value="Cash">Cash</SelectItem>
                                <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <F label="Reference" value={ref} onChange={setRef} />
                    <div>
                        <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Notes</Label>
                        <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="mt-1.5" rows={2} />
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" style={{ background: 'var(--bm-primary)' }} data-testid="payment-submit-btn">Record</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
